import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const Agenda = () => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeGroup, setActiveGroup] = useState(null);
    const { config } = useConfig();

    useEffect(() => {
        fetchAgenda();
    }, []);

    useEffect(() => {
        if (events.length > 0) {
            const availableGroups = Array.from(new Set(events.map(e => e.group_name).filter(Boolean)));
            if (availableGroups.length > 0 && !activeGroup) {
                setActiveGroup(availableGroups[0]);
            }
        }
    }, [events, activeGroup]);

    const fetchAgenda = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/agenda?status=active');
            setEvents(res.data);
        } catch (error) {
            console.error("Error fetching agenda", error);
        } finally {
            setIsLoading(false);
        }
    };

    const pageDesign = config?.pageDesigns?.agenda || {};
    const isImageOnly = pageDesign.agendaImageOnly;
    const primaryColor = config?.primaryColor || '#9d4edd';

    const getFlagEmoji = (country) => {
        if (!country) return '';
        const name = country.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const flags = {
            'colombia': '🇨🇴', 'co': '🇨🇴',
            'mexico': '🇲🇽', 'mx': '🇲🇽',
            'argentina': '🇦🇷', 'ar': '🇦🇷',
            'chile': '🇨🇱', 'cl': '🇨🇱',
            'peru': '🇵🇪', 'pe': '🇵🇪',
            'ecuador': '🇪🇨', 'ec': '🇪🇨',
            'espana': '🇪🇸', 'es': '🇪🇸',
            'estados unidos': '🇺🇸', 'usa': '🇺🇸', 'us': '🇺🇸',
            'brasil': '🇧🇷', 'brazil': '🇧🇷', 'br': '🇧🇷',
            'venezuela': '🇻🇪', 've': '🇻🇪',
            'uruguay': '🇺🇾', 'uy': '🇺🇾',
            'paraguay': '🇵🇾', 'py': '🇵🇾',
            'bolivia': '🇧🇴', 'bo': '🇧🇴',
            'panama': '🇵🇦', 'pa': '🇵🇦',
            'costa rica': '🇨🇷', 'cr': '🇨🇷',
            'cuba': '🇨🇺', 'cu': '🇨🇺',
            'republica dominicana': '🇩🇴', 'do': '🇩🇴',
            'puerto rico': '🇵🇷', 'pr': '🇵🇷',
            'honduras': '🇭🇳', 'hn': '🇭🇳',
            'el salvador': '🇸🇻', 'sv': '🇸🇻',
            'guatemala': '🇬🇹', 'gt': '🇬🇹',
            'nicaragua': '🇳🇮', 'ni': '🇳🇮',
            'italia': '🇮🇹', 'it': '🇮🇹',
            'francia': '🇫🇷', 'fr': '🇫🇷',
            'alemania': '🇩🇪', 'de': '🇩🇪',
            'reino unido': '🇬🇧', 'uk': '🇬🇧', 'gb': '🇬🇧',
            'canada': '🇨🇦', 'ca': '🇨🇦'
        };
        return flags[name] || '';
    };

    const groups = Array.from(new Set(events.map(e => e.group_name).filter(Boolean)));
    const filteredEvents = activeGroup ? events.filter(e => e.group_name === activeGroup) : events;

    // Agrupar por fecha y luego por "bloque"
    const groupedData = filteredEvents.reduce((acc, event) => {
        const dateStr = new Date(event.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const capitalizedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

        if (!acc[capitalizedDate]) acc[capitalizedDate] = {};

        const block = event.block_name || 'Sin Bloque';
        if (!acc[capitalizedDate][block]) acc[capitalizedDate][block] = {
            name: block,
            color: event.block_color || '#9d4edd',
            events: []
        };

        // Unificar ponentes si la hora y titulo coinciden
        const existingEvent = acc[capitalizedDate][block].events.find(e => e.title === event.title && e.start_time === event.start_time);

        if (existingEvent) {
            if (event.speaker_name) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.name === event.speaker_name)) {
                    existingEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
                }
            } else if (event.country) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.country === event.country)) {
                    existingEvent.speakers.push({ name: '', country: event.country });
                }
            }
        } else {
            const newEvent = { ...event, speakers: [] };
            if (event.speaker_name) {
                newEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
            } else if (event.country) {
                newEvent.speakers.push({ name: '', country: event.country });
            }
            acc[capitalizedDate][block].events.push(newEvent);
        }

        return acc;
    }, {});

    const getIcon = (type) => {
        if (type === 'breakfast') return '🥐';
        if (type === 'coffee') return '☕';
        if (type === 'lunch') return '🍽️';
        if (type === 'closing') return '✈️';
        if (type === 'other') return '✨';
        return null;
    };

    const groupColors = pageDesign.groupColors || [];
    const activeGroupConfig = groupColors.find(gc => gc.groupName === activeGroup);
    const tabActiveGradientRaw = activeGroupConfig?.gradient || pageDesign.tabActiveGradient || 'bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]';
    const tabTextColor = activeGroupConfig?.textColor || pageDesign.tabTextColor || '#ffffff';

    // Helper functions to handle dynamic arbitrary tailwind gradients (e.g. bg-[linear-gradient...])
    const getBgStyle = (gradString) => {
        if (!gradString) return {};
        const match = gradString.match(/^bg-\[(.*)\]$/);
        return match ? { background: match[1] } : {};
    };
    const getBgClass = (gradString) => {
        if (!gradString || gradString.startsWith('bg-[')) return '';
        return gradString;
    };
    const getSvgColors = (gradString) => {
        if (!gradString) return ['#e17c33', '#d1583e', '#c0344b'];
        const hexMatches = gradString.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/g);
        if (hexMatches && hexMatches.length >= 2) {
            if (hexMatches.length === 2) return [hexMatches[0], hexMatches[0], hexMatches[1]];
            return [hexMatches[0], hexMatches[1], hexMatches[2]];
        }
        return ['#e17c33', '#d1583e', '#c0344b'];
    };

    const getTextGradientStyle = (gradString) => {
        if (!gradString) return { color: 'white' };

        let background = '';
        if (gradString.startsWith('bg-[')) {
            const match = gradString.match(/^bg-\[(.*)\]$/);
            if (match) background = match[1];
        } else if (gradString.includes('linear-gradient')) {
            background = gradString;
        } else {
            const colors = getSvgColors(gradString);
            background = `linear-gradient(90deg, ${colors.join(', ')})`;
        }

        return {
            backgroundImage: background,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'transparent'
        };
    };

    const activeSvgColors = getSvgColors(tabActiveGradientRaw);

    const imageAgendas = pageDesign.imageAgendas || [];

    if (isImageOnly) {
        // Handle multiple image agendas
        if (imageAgendas.length > 0) {
            const activeImage = imageAgendas.find(a => a.group_name === activeGroup)?.staticImage || imageAgendas[0]?.staticImage;
            const currentActiveGroup = activeGroup || imageAgendas[0]?.group_name;

            return (
                <div className="animate-fadeIn max-w-5xl mx-auto pb-32 font-['Alliance-Light'] pt-10">
                    <div className="flex flex-col items-center gap-4 mb-10 pb-4">
                        <div className="flex justify-center gap-4 w-full overflow-x-auto pb-2 px-4 custom-scrollbar">
                            {imageAgendas.map(ag => {
                                const gc = groupColors.find(g => g.groupName === ag.group_name);
                                const groupGradRaw = gc?.gradient || pageDesign.tabActiveGradient || 'bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]';
                                const groupText = gc?.textColor || pageDesign.tabTextColor || '#ffffff';
                                return (
                                    <button
                                        key={ag.group_name}
                                        onClick={() => setActiveGroup(ag.group_name)}
                                        className={`px-8 py-4 rounded-full font-black uppercase tracking-widest text-[11px] transition-all whitespace-nowrap shadow-xl ${currentActiveGroup === ag.group_name
                                            ? `${getBgClass(groupGradRaw)} scale-105 border border-white/40`
                                            : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                                            }`}
                                        style={currentActiveGroup === ag.group_name ? { ...getBgStyle(groupGradRaw), color: groupText } : {}}
                                    >
                                        {ag.group_name}
                                    </button>
                                );
                            })}
                        </div>

                        {(() => {
                            const activeAg = imageAgendas.find(a => a.group_name === currentActiveGroup) || imageAgendas[0];
                            if (activeAg?.downloadUrl) {
                                return (
                                    <a
                                        href={activeAg.downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-white/10 text-white px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all border border-white/20 shadow-lg flex items-center gap-2"
                                    >
                                        <span>⬇️</span> Descargar Agenda
                                    </a>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    <div className="flex flex-col items-center p-0 bg-transparent">
                        {activeImage ? (
                            <img
                                src={activeImage}
                                alt={`Agenda ${currentActiveGroup}`}
                                className="w-full md:w-[90vw] h-auto shadow-2xl mb-10"
                            />
                        ) : (
                            <div className="text-white/50 py-10 uppercase tracking-widest font-black text-xs">Sin imagen configurada</div>
                        )}
                    </div>
                </div>
            );
        }

        // Fallback to single image
        return (
            <div className="animate-fadeIn min-h-screen flex flex-col items-center p-0 bg-transparent pt-10">
                <img
                    src={pageDesign.staticImage || ""}
                    alt="Agenda"
                    className="w-full md:w-[90vw] h-auto shadow-2xl mb-10"
                />
            </div>
        );
    }

    const showTitle = pageDesign.showTitle !== false;
    const pageTitle = pageDesign.title || 'Agenda';
    const pageSubtitle = pageDesign.subtitle || '';

    return (
        <div className="animate-fadeIn max-w-5xl mx-auto pb-32 font-['Alliance-Light']">
            {showTitle && (
                <header className="mb-12 text-center pt-8">
                    <h1
                        className="text-4xl sm:text-5xl font-black uppercase tracking-tighter italic"
                        style={getTextGradientStyle(tabActiveGradientRaw)}
                    >
                        {pageTitle}
                    </h1>
                    <p className="text-white/80 text-[10px] font-black uppercase tracking-[0.4em] mt-2">{pageSubtitle}</p>
                </header>
            )}

            {/* SVG Gradient Definition */}
            <svg width="0" height="0" className="absolute">
                <defs>
                    <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={activeSvgColors[0]} />
                        <stop offset="50%" stopColor={activeSvgColors[1]} />
                        <stop offset="100%" stopColor={activeSvgColors[2]} />
                    </linearGradient>
                </defs>
            </svg>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-12 h-12 border-4 border-white/10 border-t-purple-500 rounded-full animate-spin"></div>
                    <p className="text-white/50 text-[10px] font-black uppercase tracking-[0.4em] mt-6 animate-pulse">Cargando Agenda...</p>
                </div>
            ) : (
                <div className="space-y-16">
                    {groups.length > 0 && (
                        <div className="flex flex-col items-center gap-4 mb-10 pb-4">
                            <div className="flex justify-center gap-4 w-full overflow-x-auto pb-2 px-4 custom-scrollbar">
                                {groups.map(group => {
                                    const gc = groupColors.find(g => g.groupName === group);
                                    const groupGradRaw = gc?.gradient || pageDesign.tabActiveGradient || 'bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]';
                                    const groupText = gc?.textColor || pageDesign.tabTextColor || '#ffffff';
                                    return (
                                        <button
                                            key={group}
                                            onClick={() => setActiveGroup(group)}
                                            className={`px-8 py-4 rounded-full font-black uppercase tracking-widest text-[11px] transition-all whitespace-nowrap shadow-xl ${activeGroup === group
                                                ? `${getBgClass(groupGradRaw)} scale-105 border border-white/40`
                                                : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                                                }`}
                                            style={activeGroup === group ? { ...getBgStyle(groupGradRaw), color: groupText } : {}}
                                        >
                                            {group}
                                        </button>
                                    );
                                })}
                            </div>

                            {(() => {
                                const gc = groupColors.find(g => g.groupName === activeGroup);
                                if (gc?.downloadUrl) {
                                    return (
                                        <a
                                            href={gc.downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-white/10 text-white px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all border border-white/20 shadow-lg flex items-center gap-2"
                                        >
                                            <span>⬇️</span> Descargar Agenda
                                        </a>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    )}

                    {Object.keys(groupedData).map((date, dayIdx) => (
                        <div key={dayIdx} className="space-y-8">
                            {/* Cabecera de Día */}
                            <div className="flex items-center gap-4 mb-4">
                                <div className="bg-white/10 w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white/20">🗓️</div>
                                <div className={`${getBgClass(tabActiveGradientRaw)} px-8 py-3 rounded-full shadow-xl border border-white/40`} style={getBgStyle(tabActiveGradientRaw)}>
                                    <h2 className={`text-lg sm:text-xl font-black uppercase tracking-widest`} style={{ color: tabTextColor }}>{date}</h2>
                                </div>
                            </div>

                            {/* Bloques del Día */}
                            <div className="space-y-4">
                                {/* Columns Header */}
                                {/* Columns Header */}
                                <div className="flex w-full gap-2 sm:gap-8 mb-2 px-2 sm:px-6">
                                    <div className="w-16 sm:w-32 shrink-0 relative bg-gradient-to-r from-[#220042] to-[#d1583e]/40 border border-[#e17c33]/30 rounded-full py-1 sm:py-2 flex items-center shadow-lg h-8 sm:h-10 justify-center sm:justify-start">
                                        <div className="hidden sm:flex absolute -left-4 w-10 h-10 bg-[#1c0042] rounded-full border border-[#e17c33]/50 items-center justify-center shadow-[0_0_15px_rgba(225,124,51,0.5)]">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="url(#iconGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                        </div>
                                        <span className="sm:ml-8 text-white text-[8px] sm:text-[12px] font-black tracking-widest uppercase">Hora</span>
                                    </div>
                                    <div className="flex-grow relative bg-gradient-to-r from-[#220042] to-[#d1583e]/40 border border-[#e17c33]/30 rounded-full py-1 sm:py-2 flex items-center shadow-lg h-8 sm:h-10 justify-center sm:justify-start">
                                        <div className="hidden sm:flex absolute -left-4 w-10 h-10 bg-[#1c0042] rounded-full border border-[#e17c33]/50 items-center justify-center shadow-[0_0_15px_rgba(225,124,51,0.5)]">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="url(#iconGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                                        </div>
                                        <span className="sm:ml-8 text-white text-[8px] sm:text-[12px] font-black tracking-widest uppercase">Tema</span>
                                    </div>
                                    <div className="w-20 sm:w-48 shrink-0 relative bg-gradient-to-r from-[#220042] to-[#d1583e]/40 border border-[#e17c33]/30 rounded-full py-1 sm:py-2 flex items-center shadow-lg h-8 sm:h-10 justify-center sm:justify-start">
                                        <div className="hidden sm:flex absolute -left-4 w-10 h-10 bg-[#1c0042] rounded-full border border-[#e17c33]/50 items-center justify-center shadow-[0_0_15px_rgba(225,124,51,0.5)]">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="url(#iconGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                                        </div>
                                        <span className="sm:ml-8 text-white text-[8px] sm:text-[12px] font-black tracking-widest uppercase">Speaker</span>
                                    </div>
                                </div>

                                {Object.values(groupedData[date]).map((block, blockIdx) => (
                                    <React.Fragment key={blockIdx}>
                                        {block.name !== 'Sin Bloque' && (
                                            <div className="px-6 py-2 rounded-full font-black uppercase text-[11px] tracking-[0.2em] italic w-max mt-6 shadow-md border border-white/10" style={{ backgroundColor: `${block.color}44`, color: '#FFFFFF' }}>
                                                {block.name}
                                            </div>
                                        )}
                                        {block.events.map((event, i) => {
                                            const icon = getIcon(event.activity_type);
                                            const isSpecial = event.activity_type !== 'session';

                                            let pillClass = "rounded-2xl sm:rounded-[30px] p-2 sm:p-4 flex flex-row items-center gap-2 sm:gap-4 shadow-xl transform transition-transform hover:scale-[1.01] ";

                                            if (!isSpecial) {
                                                pillClass += "bg-[#220042]/80 backdrop-blur-md border border-[#b669e4]/50";
                                            } else {
                                                // Dynamic colors for special events
                                                pillClass += `${getBgClass(tabActiveGradientRaw)} border border-white/40 `;
                                            }

                                            const isDarkText = false; // Legacy fallback

                                            return (
                                                <div key={i} className={pillClass} style={isSpecial ? getBgStyle(tabActiveGradientRaw) : {}}>
                                                    {/* Hora */}
                                                    <div className={`w-16 sm:w-32 shrink-0 text-center font-black tracking-tighter sm:tracking-widest text-[9px] sm:text-[13px] ${isSpecial ? '' : 'text-yellow-400'}`} style={isSpecial ? { color: tabTextColor } : {}}>
                                                        {event.start_time.slice(0, 5)}<br className="sm:hidden" /><span className="hidden sm:inline"> - </span>{event.end_time.slice(0, 5)}
                                                    </div>

                                                    {/* Tema */}
                                                    <div className="flex-grow text-center px-1 sm:px-4">
                                                        <h3 className={`text-[10px] sm:text-[14px] font-black leading-tight ${isSpecial ? '' : 'text-white'}`} style={isSpecial ? { color: tabTextColor } : {}}>
                                                            {event.title}
                                                        </h3>
                                                        {event.moderator && (
                                                            <p className={`text-[8px] sm:text-[10px] font-bold mt-1 uppercase tracking-widest ${isSpecial ? '' : 'text-white/50'}`} style={isSpecial ? { color: tabTextColor, opacity: 0.8 } : {}}>Mod: {event.moderator}</p>
                                                        )}
                                                    </div>

                                                    {/* Speaker o Icono */}
                                                    <div className="w-20 sm:w-48 shrink-0 flex items-center justify-center sm:justify-end pr-0 sm:pr-4">
                                                        {isSpecial && icon ? (
                                                            <div className={`text-xl sm:text-3xl filter drop-shadow-sm`} style={{ color: tabTextColor }}>{icon}</div>
                                                        ) : (
                                                            <div className="flex flex-col gap-1 text-center sm:text-right">
                                                                {event.speakers.map((speaker, idx) => (
                                                                    <div key={idx} className="flex flex-col sm:flex-row items-center justify-center sm:justify-end gap-0 sm:gap-2">
                                                                        <span className={`text-[9px] sm:text-[12px] font-black tracking-tighter leading-tight ${isSpecial ? '' : 'text-yellow-400/90'}`} style={isSpecial ? { color: tabTextColor } : {}}>{speaker.name}</span>
                                                                        {speaker.country && (
                                                                            <span className="text-[10px] sm:text-sm filter drop-shadow-md" title={speaker.country}>
                                                                                {getFlagEmoji(speaker.country)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
};

export default Agenda;
